import React, { useState, useEffect } from 'react';

/**
 * Componente que mostra notificação quando há uma atualização disponível do PWA
 * Escuta mensagens do Service Worker e oferece opção de atualizar
 */
const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('Nova versão disponível!');

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Ouvir mensagens do Service Worker
    const handleSWMessage = (event) => {
      const { data } = event;
      
      if (data?.type === 'SW_ACTIVATED') {
        console.log(`[UpdateNotification] SW ativado - versão ${data.version}`);
        if (data.forceReload) {
          setUpdateMessage(`Atualização v${data.version} aplicada! Recarregando...`);
          setShowUpdate(true);
          // Dar tempo para o usuário ver a mensagem
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      }
      
      if (data?.type === 'RELOAD_PAGE') {
        console.log('[UpdateNotification] Recebido comando de reload');
        setUpdateMessage('Atualizando aplicação...');
        setShowUpdate(true);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleSWMessage);

    // Verificar se há uma atualização pendente
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        setShowUpdate(true);
        setUpdateMessage('Nova versão disponível! Clique para atualizar.');
      }
    });

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    };
  }, []);

  const handleUpdate = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
    // O reload será acionado pelo controllerchange event
    setUpdateMessage('Aplicando atualização...');
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  // Forçar limpeza de cache manualmente
  const handleForceClearCache = async () => {
    setUpdateMessage('Limpando cache...');
    
    // Limpar caches via Service Worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
    }
    
    // Limpar caches diretamente também
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    
    // Recarregar a página com bypass de cache
    setTimeout(() => {
      window.location.reload(true);
    }, 500);
  };

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[9999] animate-in slide-in-from-bottom-5 duration-300">
      <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A] border border-[#C79F5E]/50 rounded-xl shadow-2xl shadow-[#C79F5E]/20 p-4">
        <div className="flex items-start gap-3">
          {/* Ícone */}
          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-[#C79F5E] to-[#A67C3D] rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          
          {/* Conteúdo */}
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-semibold text-sm">
              Atualização Disponível
            </h4>
            <p className="text-gray-400 text-xs mt-0.5">
              {updateMessage}
            </p>
            
            {/* Botões */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleUpdate}
                className="flex-1 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:from-[#D4AF6E] hover:to-[#B68D4D] text-black text-xs font-bold py-2 px-3 rounded-lg transition-all"
              >
                Atualizar Agora
              </button>
              <button
                onClick={handleForceClearCache}
                className="bg-[#333] hover:bg-[#444] text-white text-xs font-medium py-2 px-3 rounded-lg transition-all"
                title="Forçar limpeza de cache"
              >
                🔄
              </button>
              <button
                onClick={handleDismiss}
                className="bg-[#333] hover:bg-[#444] text-gray-400 hover:text-white text-xs font-medium py-2 px-3 rounded-lg transition-all"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;
